import { FeedbackModal, showToast } from "@/components/feedback"
import { Button, Card, Text } from "@/components/ui"
import { cancelScheduledOrder } from "@/lib/cancel-scheduled-order"
import {
  canCancelScheduledOrder,
  getPreparationStartAt,
  getScheduledReadyAt,
  isPreparationStartDegraded,
} from "@/lib/order-status"
import { formatWindowOpening } from "@/lib/scheduling/format"
import { getTenantTimezone } from "@/lib/scheduling/tenant-timezone"
import type { OrderResponse } from "@/types/external"
import { useState } from "react"

type CancelScheduledOrderActionProps = {
  order: OrderResponse
  /** Applies the cancelled order the backend returned, ahead of the next poll. */
  onCancelled: (order: OrderResponse | null) => void
}

/**
 * "Cancelar agendamento" on the order tracking screen: offered only while the
 * kitchen has not started (BR-11), and always re-checked server-side — a 409
 * carrying `refusal` is surfaced as the reason, not as a generic failure.
 */
export function CancelScheduledOrderAction({
  order,
  onCancelled,
}: CancelScheduledOrderActionProps) {
  const [confirming, setConfirming] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const timezone = getTenantTimezone()

  if (!canCancelScheduledOrder(order)) return null

  const readyAt = getScheduledReadyAt(order)
  const preparationStart = getPreparationStartAt(order)
  const scheduledLabel = readyAt
    ? formatWindowOpening(readyAt, new Date(), timezone)
    : null
  const cutOffLabel = preparationStart
    ? formatWindowOpening(preparationStart.toISOString(), new Date(), timezone)
    : null
  const degraded = isPreparationStartDegraded(order)

  async function handleConfirm() {
    setSubmitting(true)
    const result = await cancelScheduledOrder(order.id)
    setSubmitting(false)
    setConfirming(false)

    if (result.outcome === "cancelled") {
      showToast.success("Agendamento cancelado.")
      onCancelled(result.order)
      return
    }
    showToast.error(result.message)
  }

  return (
    <>
      <Button
        variant="outline"
        tone="danger"
        size="lg"
        fullWidth
        onClick={() => setConfirming(true)}
      >
        Cancelar agendamento
      </Button>

      <FeedbackModal
        open={confirming}
        onOpenChange={(next) => {
          if (!submitting) setConfirming(next)
        }}
        variant="warning"
        title="Cancelar este agendamento?"
        confirmLabel="Sim, cancelar"
        confirmTone="danger"
        cancelLabel="Manter agendamento"
        loading={submitting}
        onConfirm={handleConfirm}
        description={
          <p>
            {scheduledLabel
              ? `Seu pedido está agendado para ${scheduledLabel}.`
              : "Seu pedido está agendado."}{" "}
            Ao cancelar, ele não será preparado.
          </p>
        }
      >
        <Card variant="tinted" padding="sm" className="flex flex-col gap-2">
          <Text size="sm" tone="muted">
            Se você pagou pelo app, o valor é estornado quando você cancela antes do preparo
            começar — e também quando a loja cancela o pedido.
          </Text>
          <Text size="sm" tone="muted">
            {cutOffLabel
              ? `Depois que o preparo começa (${cutOffLabel}) o cancelamento por aqui não vale mais: fale com a loja.`
              : "Depois que o preparo começa, o cancelamento por aqui não vale mais: fale com a loja."}
          </Text>
          {degraded ? (
            <Text size="sm" tone="muted">
              A loja ainda pode começar o preparo um pouco antes desse horário.
            </Text>
          ) : null}
        </Card>
      </FeedbackModal>
    </>
  )
}
